import { faCalendarDay } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fromZonedTime } from 'date-fns-tz';
import { memo, useEffect, useMemo, useState } from 'react';

import defaultTimeZone from '@/constants/defaultTimeZone';
import emptySelectOption from '@/constants/emptySelectOption';
import { useAppointmentTypeOptions } from '@/hooks/useAppointmentTypes/useAppointmentTypes';
import useNavigateToPage from '@/hooks/useNavigateToPage';
import { useCreateFutureAppointmentRequestForDate } from '@/hooks/useScheduleExpectations/useScheduleExpectations';
import { FutureAppointmentRequestForDatePayload } from '@/types/scheduleExpectation';
import {
  Alert,
  Checkbox,
  Datepicker,
  Input,
  InputGroup,
  Modal,
  RadioGroup,
  Select,
  Textarea
} from '@/ui';
import type { OptionType } from '@/ui/components/Form/Select/types';
import {
  addDaysToDateString,
  getNextMonday,
  getThisWeeksMonday,
  parseIsoDate,
  replaceTimezone
} from '@/utils/dates';

import RepeatTimelinePreview from './RepeatTimelinePreview';
import { FarDateOption, FarModifier, type FarConstraint, type FarMode, type Props } from './types';

const DATE_OPTIONS = [
  { label: 'Pick a date', value: FarDateOption.PICK_A_DATE },
  { label: 'In a few weeks', value: FarDateOption.IN_A_FEW_WEEKS },
  { label: 'ASAP', value: FarDateOption.ASAP }
];

const MODIFIER_OPTIONS = [
  { label: 'No later than', value: FarModifier.NO_LATER_THAN },
  { label: 'No earlier than', value: FarModifier.NO_EARLIER_THAN },
  { label: 'Around', value: FarModifier.AROUND },
  { label: 'Exactly', value: FarModifier.EXACTLY }
];

const MODIFIER_TO_MODE_CONSTRAINT: Record<
  FarModifier,
  { mode: FarMode; constraint: FarConstraint }
> = {
  [FarModifier.NO_LATER_THAN]: { mode: 'before', constraint: 'approximate' },
  [FarModifier.NO_EARLIER_THAN]: { mode: 'after', constraint: 'approximate' },
  [FarModifier.AROUND]: { mode: 'on', constraint: 'approximate' },
  [FarModifier.EXACTLY]: { mode: 'on', constraint: 'precise' }
};

const mondayOfWeek = (dateStr: string): string => {
  const day = parseIsoDate(dateStr).getDay(); // 0 = Sun, 1 = Mon, ..., 6 = Sat
  const daysSinceMonday = (day + 6) % 7;
  return addDaysToDateString(dateStr, -daysSinceMonday);
};

const sundayOfWeek = (dateStr: string): string => addDaysToDateString(mondayOfWeek(dateStr), 6);

// dateStr's Y/M/D fields are the intended calendar day in the member's
// timezone, but parseIsoDate's Date is anchored to the browser's local
// timezone. fromZonedTime re-anchors those same Y/M/D fields to the correct
// UTC instant for `timeZone`, so formatting it back with that same timeZone
// round-trips to the intended day regardless of the browser's own timezone.
const formatBoundaryDate = (dateStr: string, timeZone: string): string => {
  const anchored = fromZonedTime(parseIsoDate(dateStr), timeZone);
  return anchored.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'long',
    day: 'numeric',
    timeZone
  });
};

// Describes the cycle (Monday-Sunday) a target date falls in, bounded by the
// chosen modifier: "no later than" narrows the top end to the target date
// itself, "no earlier than" narrows the bottom end, "exactly" collapses to a
// single day, and "around" (also used for ASAP/"in a few weeks", which have
// no modifier of their own) is the full cycle.
const describeCycleRangePhrase = (
  modifier: FarModifier,
  dateStr: string,
  timeZone: string
): string => {
  const start = formatBoundaryDate(mondayOfWeek(dateStr), timeZone);
  const end = formatBoundaryDate(sundayOfWeek(dateStr), timeZone);
  const picked = formatBoundaryDate(dateStr, timeZone);

  switch (modifier) {
    case FarModifier.NO_LATER_THAN:
      return `between ${start} and ${picked}`;
    case FarModifier.NO_EARLIER_THAN:
      return `between ${picked} and ${end}`;
    case FarModifier.EXACTLY:
      return `exactly on ${picked}`;
    case FarModifier.AROUND:
    default:
      return `between ${start} and ${end}`;
  }
};

const describeCycleRange = (modifier: FarModifier, dateStr: string, timeZone: string): string =>
  `We'll schedule the appointment ${describeCycleRangePhrase(modifier, dateStr, timeZone)}.`;

const formatShortDate = (dateStr: string, timeZone: string): string => {
  const anchored = fromZonedTime(parseIsoDate(dateStr), timeZone);
  return anchored.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone });
};

// Compact counterpart to describeCycleRangePhrase for the timeline preview's
// per-dot labels, where a full "between X and Y" sentence doesn't fit.
const describeShortRange = (modifier: FarModifier, dateStr: string, timeZone: string): string => {
  const start = formatShortDate(mondayOfWeek(dateStr), timeZone);
  const end = formatShortDate(sundayOfWeek(dateStr), timeZone);
  const picked = formatShortDate(dateStr, timeZone);

  switch (modifier) {
    case FarModifier.NO_LATER_THAN:
      return `${start}–${picked}`;
    case FarModifier.NO_EARLIER_THAN:
      return `${picked}–${end}`;
    case FarModifier.EXACTLY:
      return picked;
    case FarModifier.AROUND:
    default:
      return `${start}–${end}`;
  }
};

const MAX_REPEAT_TIMES = 3;

const FutureAppointmentRequestForDateModal = ({
  initialAppointmentTypeId,
  onClose,
  onSuccess,
  open,
  person
}: Props) => {
  const { episode_of_care, time_zone } = person;
  const currentActivePhase = episode_of_care?.current_active_phase;
  const timeZone = time_zone || defaultTimeZone;

  const [appointmentType, setAppointmentType] = useState<OptionType<string>>(emptySelectOption);
  const [dateOption, setDateOption] = useState<FarDateOption>(FarDateOption.PICK_A_DATE);
  const [weeksOut, setWeeksOut] = useState('1');
  const [pickedDate, setPickedDate] = useState<string | null>(null);
  const [modifier, setModifier] = useState<FarModifier>(FarModifier.AROUND);
  const [repeatEnabled, setRepeatEnabled] = useState(false);
  const [repeatTimes, setRepeatTimes] = useState('1');
  const [note, setNote] = useState('');
  const [dateError, setDateError] = useState<string | null>(null);

  const { data: appointmentTypesResponse, isPending: isPendingAppointmentTypes } =
    useAppointmentTypeOptions();

  const appointmentTypeOptions = useMemo(() => {
    if (!appointmentTypesResponse) return [];
    return appointmentTypesResponse?.sort().map((type) => ({ label: type.name, value: type.id }));
  }, [appointmentTypesResponse]);

  useEffect(() => {
    if (initialAppointmentTypeId && appointmentTypeOptions.length > 0) {
      const match = appointmentTypeOptions.find((o) => o.value === initialAppointmentTypeId);
      if (match) setAppointmentType(match);
    }
  }, [initialAppointmentTypeId, appointmentTypeOptions]);

  const referenceDate = useMemo(() => replaceTimezone(new Date(), timeZone), [timeZone]);
  const nextMonday = useMemo(() => getNextMonday(referenceDate), [referenceDate]);
  const thisWeeksMonday = useMemo(() => getThisWeeksMonday(referenceDate), [referenceDate]);

  const weeksOutTargetDate = useMemo(() => {
    const weeks = Math.max(1, parseInt(weeksOut) || 1);
    return addDaysToDateString(nextMonday, (weeks - 1) * 7);
  }, [nextMonday, weeksOut]);

  const repeatTimesValue = useMemo(
    () => Math.min(MAX_REPEAT_TIMES, Math.max(1, parseInt(repeatTimes) || 1)),
    [repeatTimes]
  );

  const targetDate = useMemo(() => {
    switch (dateOption) {
      case FarDateOption.ASAP:
        return thisWeeksMonday;
      case FarDateOption.IN_A_FEW_WEEKS:
        return weeksOutTargetDate;
      case FarDateOption.PICK_A_DATE:
        return pickedDate;
      default:
        return null;
    }
  }, [dateOption, pickedDate, thisWeeksMonday, weeksOutTargetDate]);

  // Mirrors the backend's own cycle_time derivation (see
  // build_future_appointment_request_for_date): the wait is measured from
  // next Monday unless the target already falls in the current week, in
  // which case there's no wait at all. Each repeat then lands one full
  // cycle (the wait plus the target's own actionable week) after the last.
  const repeatIntervalWeeks = useMemo(() => {
    if (!targetDate) return 1;
    const targetMonday = mondayOfWeek(targetDate);
    const cycleStart = targetMonday === thisWeeksMonday ? thisWeeksMonday : nextMonday;
    const weeksUntilTarget = Math.round(
      (parseIsoDate(targetMonday).getTime() - parseIsoDate(cycleStart).getTime()) /
        (7 * 24 * 60 * 60 * 1000)
    );
    return weeksUntilTarget + 1;
  }, [targetDate, thisWeeksMonday, nextMonday]);

  const activeModifier = dateOption === FarDateOption.PICK_A_DATE ? modifier : FarModifier.AROUND;

  const timelineSteps = useMemo(() => {
    if (!repeatEnabled || !targetDate) return [];
    return Array.from({ length: repeatTimesValue + 1 }, (_, index) => {
      const occurrenceDate =
        index === 0 ? targetDate : addDaysToDateString(targetDate, index * repeatIntervalWeeks * 7);
      return {
        id: occurrenceDate,
        label: describeShortRange(activeModifier, occurrenceDate, timeZone),
        isFirst: index === 0
      };
    });
  }, [repeatEnabled, targetDate, repeatTimesValue, repeatIntervalWeeks, activeModifier, timeZone]);

  const repeatRanges = useMemo(() => {
    if (!repeatEnabled || !targetDate) return [];
    return Array.from({ length: repeatTimesValue + 1 }, (_, index) => {
      const occurrenceDate =
        index === 0 ? targetDate : addDaysToDateString(targetDate, index * repeatIntervalWeeks * 7);
      return describeCycleRangePhrase(activeModifier, occurrenceDate, timeZone);
    });
  }, [repeatEnabled, targetDate, repeatTimesValue, repeatIntervalWeeks, activeModifier, timeZone]);

  const createFutureAppointmentRequestForDate = useCreateFutureAppointmentRequestForDate(
    currentActivePhase?.id ?? ''
  );
  const { navigateToBooking } = useNavigateToPage();

  const resetForm = () => {
    setAppointmentType(emptySelectOption);
    setDateOption(FarDateOption.PICK_A_DATE);
    setWeeksOut('1');
    setPickedDate(null);
    setModifier(FarModifier.AROUND);
    setRepeatEnabled(false);
    setRepeatTimes('1');
    setNote('');
    setDateError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = (shouldNavigateToBooking: boolean) => {
    if (!targetDate) {
      setDateError('Please pick a target date');
      return;
    }
    setDateError(null);

    if (!currentActivePhase || !appointmentType.value) return;

    // The flexibility modifier only makes sense against a specific,
    // staff-picked date -- ASAP and "in a few weeks" always resolve to a
    // whole week, so they always request an approximate match against it.
    const { mode, constraint } = MODIFIER_TO_MODE_CONSTRAINT[activeModifier];
    // repeatTimesValue counts additional repeats ("repeat 1 time" = 2 total
    // occurrences), but the backend's repeat_times is the total occurrence
    // count, so it needs the +1 to actually repeat rather than being a
    // no-op equivalent to not setting it at all.
    const payload: FutureAppointmentRequestForDatePayload = {
      appointment_type: appointmentType.value,
      phase: currentActivePhase.id,
      target_at: targetDate,
      mode,
      constraint,
      repeat_times: repeatEnabled ? repeatTimesValue + 1 : 1,
      note: note.trim() || null
    };

    createFutureAppointmentRequestForDate.mutate(
      { data: payload },
      {
        onSuccess: (response) => {
          const submittedAppointmentTypeId = appointmentType.value;
          handleClose();
          onSuccess?.(submittedAppointmentTypeId);

          if (shouldNavigateToBooking) {
            const submittedAppointmentType = appointmentTypesResponse?.find(
              (type) => type.id === submittedAppointmentTypeId
            );
            const params = new URLSearchParams();
            if (submittedAppointmentType?.healthie_id) {
              params.append('appointmentType', submittedAppointmentType.healthie_id.toString());
            }
            // response is the actionable expectation for the first occurrence,
            // whose own cycle_time is always 1 week -- so start_at + that one
            // week is exactly the window the target date falls within.
            const startDate = new Date(response.start_at);
            params.append('startDate', startDate.toISOString());
            const endDate = new Date(
              startDate.getTime() + response.cycle_time * 7 * 24 * 60 * 60 * 1000 - 1000
            );
            params.append('endDate', endDate.toISOString());
            navigateToBooking(person.id, params);
          }
        }
      }
    );
  };

  const renderBody = () => {
    if (!episode_of_care) {
      return <Alert variant="info" message="This person has no episode of care" />;
    }

    if (!currentActivePhase) {
      return <Alert variant="info" message="This person has no active phase" />;
    }

    return (
      <>
        <InputGroup>
          <Select
            clearable={false}
            label="Appointment Type"
            onChange={(value) => setAppointmentType(value ?? emptySelectOption)}
            options={appointmentTypeOptions}
            selectedOption={appointmentType}
            placeholder={isPendingAppointmentTypes ? 'Loading...' : 'Select appointment type...'}
          />
        </InputGroup>

        <InputGroup>
          <RadioGroup
            id="far-date-option"
            label="When should this appointment happen?"
            onChange={(value) => setDateOption(value as FarDateOption)}
            options={DATE_OPTIONS}
            selectedValue={dateOption}
          />
        </InputGroup>

        {dateOption === FarDateOption.ASAP && (
          <InputGroup>
            <Alert variant="info" dismissable={false}>
              {repeatRanges.length > 0 ? (
                <>
                  <p className="text-sm">
                    We&apos;ll schedule the appointment on the following dates:
                  </p>
                  <ul className="mt-1 list-disc space-y-0.5 pl-4 text-sm">
                    {repeatRanges.map((range) => (
                      <li key={range}>{range}</li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="text-sm">
                  {describeCycleRange(FarModifier.AROUND, thisWeeksMonday, timeZone)}
                </p>
              )}
            </Alert>
          </InputGroup>
        )}

        {dateOption === FarDateOption.IN_A_FEW_WEEKS && (
          <InputGroup>
            <div className="mb-2 w-32">
              <Input
                id="weeks-out"
                label="In how many weeks?"
                min={1}
                max={52}
                type="number"
                onChange={(e) => setWeeksOut(e.target.value)}
                value={weeksOut}
              />
            </div>
            <Alert variant="info" dismissable={false}>
              {repeatRanges.length > 0 ? (
                <>
                  <p className="text-sm">
                    We&apos;ll schedule the appointment on the following dates:
                  </p>
                  <ul className="mt-1 list-disc space-y-0.5 pl-4 text-sm">
                    {repeatRanges.map((range) => (
                      <li key={range}>{range}</li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="text-sm">
                  {describeCycleRange(FarModifier.AROUND, weeksOutTargetDate, timeZone)}
                </p>
              )}
            </Alert>
          </InputGroup>
        )}

        {dateOption === FarDateOption.PICK_A_DATE && (
          <InputGroup>
            <div className="flex items-start gap-2">
              <div className="flex w-40 flex-col gap-1">
                {/* Invisible label matching Datepicker's own label line, so the
                    two input rows below line up regardless of what either
                    field renders underneath (Datepicker always shows a date
                    caption; this Select never does). */}
                <span
                  aria-hidden="true"
                  className="invisible block text-sm font-medium text-uniform-gray-700 dark:text-uniform-gray-300">
                  Target date
                </span>
                <Select
                  ariaLabel="Flexibility"
                  clearable={false}
                  onChange={(value) => setModifier((value?.value as FarModifier) ?? modifier)}
                  options={MODIFIER_OPTIONS}
                  selectedOption={MODIFIER_OPTIONS.find((option) => option.value === modifier)}
                />
              </div>
              <div className="flex-1">
                <Datepicker
                  label="Target date"
                  minDate={new Date(`${nextMonday}T00:00:00`)}
                  name="pick-a-date"
                  onChange={setPickedDate}
                  preventOpenOnFocus={true}
                  value={pickedDate}
                />
              </div>
            </div>
            {dateError && <p className="mt-1 text-sm text-red-600">{dateError}</p>}
            {pickedDate && (
              <Alert variant="info" dismissable={false} className="mt-2">
                {repeatRanges.length > 0 ? (
                  <>
                    <p className="text-sm">
                      We&apos;ll schedule the appointment on the following dates:
                    </p>
                    <ul className="mt-1 list-disc space-y-0.5 pl-4 text-sm">
                      {repeatRanges.map((range) => (
                        <li key={range}>{range}</li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <p className="text-sm">{describeCycleRange(modifier, pickedDate, timeZone)}</p>
                )}
              </Alert>
            )}
          </InputGroup>
        )}

        <InputGroup>
          <div className="flex items-center gap-2">
            <Checkbox
              checked={repeatEnabled}
              id="far-repeat-enabled"
              label={repeatEnabled ? 'Repeat' : 'Repeat?'}
              onChange={setRepeatEnabled}
            />
            {repeatEnabled && (
              <>
                <div className="w-20">
                  <Input
                    id="far-repeat-times"
                    hideLabel
                    label="Repeat times"
                    min={1}
                    max={MAX_REPEAT_TIMES}
                    type="number"
                    onChange={(e) => setRepeatTimes(e.target.value)}
                    value={repeatTimes}
                  />
                </div>
                <span className="text-sm text-uniform-gray-700 dark:text-uniform-gray-300">
                  times
                </span>
              </>
            )}
          </div>
          {repeatEnabled && (
            <p className="mt-1 text-xs text-uniform-gray-500 dark:text-uniform-gray-400">
              The scheduling ranges may change if a member doesn&apos;t attend one or more
              appointments
            </p>
          )}
          {repeatEnabled && timelineSteps.length > 1 && (
            <div className="mt-3">
              <RepeatTimelinePreview steps={timelineSteps} gapWeeks={repeatIntervalWeeks} />
            </div>
          )}
        </InputGroup>

        <InputGroup className="mb-0">
          <Textarea
            id="far-for-date-note"
            labelText="Note (optional)"
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add context for this appointment request"
            rows={3}
            value={note}
          />
        </InputGroup>
      </>
    );
  };

  const canSubmit =
    !!currentActivePhase && !!appointmentType.value && !!episode_of_care && !!targetDate;

  return (
    <Modal
      dataTestId="future-appointment-request-for-date-modal"
      open={open}
      onClose={handleClose}
      title={
        <>
          <FontAwesomeIcon icon={faCalendarDay} className="mr-2 text-uniform-gray-400" />
          Submit Future Appointment Request
        </>
      }
      primaryAction={
        episode_of_care && currentActivePhase
          ? {
              label: 'Submit and Book',
              onClick: () => handleSubmit(true),
              isLoading: createFutureAppointmentRequestForDate.isPending,
              disabled: !canSubmit || createFutureAppointmentRequestForDate.isPending
            }
          : undefined
      }
      tertiaryAction={
        episode_of_care && currentActivePhase
          ? {
              label: 'Submit',
              onClick: () => handleSubmit(false),
              isLoading: createFutureAppointmentRequestForDate.isPending,
              disabled: !canSubmit || createFutureAppointmentRequestForDate.isPending
            }
          : undefined
      }
      secondaryAction={{ label: 'Cancel', onClick: handleClose }}>
      {renderBody()}
    </Modal>
  );
};

export default memo(FutureAppointmentRequestForDateModal);
